/**
 * Guest Survey Page - YUL ON TAP
 * 
 * Phase 2: In-room arrival survey
 * Phase 3: Full i18n support (EN/FR)
 * Phase 3.5: Dynamic questions from hotel_survey_definitions
 * 
 * Guest taps NFC → Lands here → Completes survey
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { engagementAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Star, Loader2, CheckCircle, Building2, ChevronRight } from 'lucide-react';

const RatingQuestion = ({ value, onChange, label }) => (
  <div className="space-y-3">
    <p className="text-sm font-medium text-gray-700">{label}</p>
    <div className="flex justify-center space-x-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={`w-8 h-8 ${
              star <= value
                ? 'fill-green-500 text-green-500'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  </div>
);

const YesNoNeutralQuestion = ({ value, onChange, label, t }) => (
  <div className="space-y-3">
    <p className="text-sm font-medium text-gray-700">{label}</p>
    <RadioGroup value={value} onValueChange={onChange}>
      <div className="space-y-2">
        <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
          <RadioGroupItem value="yes" id="yes" />
          <Label htmlFor="yes" className="flex-1 cursor-pointer">
            {t('guest.survey.answers.expectationsYes')}
          </Label>
        </div>
        <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
          <RadioGroupItem value="partially" id="partially" />
          <Label htmlFor="partially" className="flex-1 cursor-pointer">
            {t('guest.survey.answers.expectationsPartial')}
          </Label>
        </div>
        <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
          <RadioGroupItem value="no" id="no" />
          <Label htmlFor="no" className="flex-1 cursor-pointer">
            {t('guest.survey.answers.expectationsNo')}
          </Label>
        </div>
      </div>
    </RadioGroup>
  </div>
);

export default function GuestSurvey() {
  const { t, i18n } = useTranslation();
  const { hotelId, roomId } = useParams();
  const [searchParams] = useSearchParams();
  const tagId = searchParams.get('tag');
  const langParam = searchParams.get('lang');

  const [pageData, setPageData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  
  // Dynamic responses keyed by question ID
  const [responses, setResponses] = useState({});
  const [additionalComments, setAdditionalComments] = useState('');

  useEffect(() => {
    if (langParam && ['en', 'fr'].includes(langParam)) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  useEffect(() => {
    fetchPageData();
  }, [hotelId, roomId]);

  const fetchPageData = async () => {
    try {
      const lang = langParam || i18n.language || 'en';
      const response = await engagementAPI.getSurveyPageData(hotelId, roomId, tagId, lang);
      setPageData(response.data);
      setQuestions(response.data.questions || []);
    } catch (err) {
      setError(err.response?.data?.detail || t('guest.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[step];
  const isLastQuestion = step === questions.length - 1;
  const isCommentsStep = step === questions.length;

  const handleNext = () => {
    if (!isCommentsStep && currentQuestion) {
      const answer = responses[currentQuestion.id];
      if (!answer) {
        if (currentQuestion.type === 'rating' || currentQuestion.type === 'yes_no_neutral') {
          toast.error(currentQuestion.type === 'rating' ? t('guest.survey.pleaseRate') : t('guest.survey.pleaseAnswer'));
          return;
        }
      }
    }
    
    if (step < questions.length) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const setResponse = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Format responses for the dynamic endpoint
      const formattedResponses = questions.map(q => ({
        question_id: q.id,
        answer: responses[q.id]
      }));
      
      await engagementAPI.submitDynamicSurvey(
        hotelId,
        roomId,
        {
          responses: formattedResponses,
          additional_comments: additionalComments || null
        },
        tagId
      );
      setSubmitted(true);
      toast.success(t('guest.survey.thankYou'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('guest.errors.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">{t('guest.survey.thankYou')}</h2>
            <p className="text-gray-600">
              {t('guest.survey.thankYouMessage', { hotelName: pageData?.hotel?.name })}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalSteps = questions.length + 1; // +1 for comments

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-2">
            <Building2 className="w-7 h-7 text-green-600" />
          </div>
          <CardTitle className="text-xl">{pageData?.hotel?.name}</CardTitle>
          <CardDescription>
            {pageData?.room?.identifier && `${t('table.room')} ${pageData.room.identifier} • `}
            {t('guest.survey.title')}
          </CardDescription>
          {/* Progress indicator */}
          <div className="flex justify-center space-x-1 mt-3">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-8 rounded-full ${
                  i <= step ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dynamic Question Rendering */}
          {!isCommentsStep && currentQuestion && (
            currentQuestion.type === 'rating' ? (
              <RatingQuestion
                label={currentQuestion.question}
                value={responses[currentQuestion.id] || 0}
                onChange={(val) => setResponse(currentQuestion.id, val)}
              />
            ) : currentQuestion.type === 'yes_no_neutral' ? (
              <YesNoNeutralQuestion
                label={currentQuestion.question}
                value={responses[currentQuestion.id] || ''}
                onChange={(val) => setResponse(currentQuestion.id, val)}
                t={t}
              />
            ) : (
              <RatingQuestion
                label={currentQuestion.question}
                value={responses[currentQuestion.id] || 0}
                onChange={(val) => setResponse(currentQuestion.id, val)}
              />
            )
          )}

          {/* Comments Step */}
          {isCommentsStep && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {t('guest.survey.questions.comments')} ({t('common.optional')})
              </p>
              <Textarea
                data-testid="survey-comments"
                placeholder={t('guest.survey.commentsPlaceholder')}
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            {step > 0 ? (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-gray-600"
              >
                {t('common.back')}
              </Button>
            ) : (
              <div />
            )}
            
            {!isCommentsStep ? (
              <Button
                data-testid="survey-next-btn"
                onClick={handleNext}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {t('common.next')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="submit-survey-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('guest.survey.submitting')}
                  </>
                ) : (
                  t('guest.survey.submitSurvey')
                )}
              </Button>
            )}
          </div>

          <p className="text-xs text-gray-400 text-center">
            {t('guest.survey.feedbackHelps')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
